'use client';

import { useRef } from 'react';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { Video } from '@flipster/react/design-utils/video';
import { css } from '@flipster/styled-system/css';
import { center, flex, vstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { MainEvent } from '@flipster/utils/segment/events/main';
import { ProdStaticPath } from '@flipster/utils/url';

import { maxWStylesWithMargin } from '@web/app/[locale]/(web)/_main-page/_styles';
import { useMainPageIntersectionSegment } from '@web/app/[locale]/(web)/_main-page/hooks/useIntersectionSegment';

import { DownloadAppButton } from '../download-app-button';

const SpotHero = () => {
  const intersectionRef = useRef<HTMLDivElement>(null);
  useMainPageIntersectionSegment(intersectionRef, 'Flipster enters the UAE');

  const { isLoggedIn } = useAuth();

  return (
    <>
      <Video
        poster={{
          base: `${ProdStaticPath}/images/main-hero-mb.webp`,
          tablet: `${ProdStaticPath}/images/main-hero-dt.webp`,
        }}
        src={{
          base: `${ProdStaticPath}/videos/main-hero-mb.mp4`,
          tablet: `${ProdStaticPath}/videos/main-hero-dt.mp4`,
        }}
        css={{
          zIndex: -1,
          pos: 'absolute',
          top: 0,
          left: '50%',
          w: '100%',
          maxW: { base: 360, tablet: 1536 },
          maxH: { base: 274, tablet: 640 },
          transform: 'translateX(-50%)',
        }}
      />
      <section
        className={css(
          maxWStylesWithMargin,
          vstack.raw({
            my: { base: 56, tablet: 100 },
            pt: { base: 46, smallDesktop: 140 },
            pb: { smallDesktop: 130 },
          }),
        )}
      >
        <h1
          className={flex({
            flexDirection: { base: 'column', smallDesktop: 'row' },
            align: 'center',
            textStyle: 'Display/XXL',
            textAlign: 'center',
            color: 'inverse.white',
            gap: { smallDesktop: 16 },
            mb: { base: 4, smallDesktop: 24 },
          })}
          ref={intersectionRef}
        >
          <p>Flipster enters the </p>
          <p className={css({ color: 'content.accent.info' })}>UAE</p>
        </h1>
        <p
          className={css({
            textStyle: { base: 'Text/M/Strong', tablet: 'Title/S' },
            color: 'inverse.white',
            mb: 32,
          })}
        >
          A secure, trusted way to trade
        </p>
        {isLoggedIn ? (
          <div className={vstack({ w: '100%', maxW: { tablet: 229 }, gap: { base: 12, tablet: 32 } })}>
            <Button
              hierarchy='primary'
              color='accent'
              size='large'
              as='a'
              href='/trade/spot/latest'
              label={<FormattedMessage id='Autogen_common.r251126.s_Trade_now' />}
              onClick={() => segment.track(MainEvent.MainPageTradeNowClick)}
              css={{ w: '100%' }}
            />
            <DownloadAppButton />
          </div>
        ) : (
          <div className={css({ w: '100%' })}>
            <div className={center({ mb: 16 })}>
              <Button
                as='a'
                hierarchy='primary'
                color='accent'
                size='large'
                href='/signup'
                label={<FormattedMessage id='Common.r250401.Sign_up' />}
                onClick={() => segment.track(MainEvent.MainPageGetStartedClick)}
                css={{ w: { base: '100%', tablet: 'auto' } }}
              />
            </div>
            <div className={center()}>
              <DownloadAppButton />
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default SpotHero;
